import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { axiosService } from "shared/helpers/axios";
import logo_alt from "media/otable_alt.png";

const StyledPaper = styled(Paper)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "32px 36px",
  width: 420,
  textAlign: "center",
  background: "rgba(255, 255, 255, 0.97)",
  backdropFilter: "blur(10px)",
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(118, 75, 162, 0.15)",
}));

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.2s ease-in-out",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(118, 75, 162, 0.5)",
    },
    "&.Mui-focused": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 16px rgba(118, 75, 162, 0.15)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(118, 75, 162, 0.55)",
        borderWidth: 1,
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
      WebkitBoxShadow: "0 0 0 100px white inset",
      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
      caretColor: "rgba(0, 0, 0, 0.87)",
      backgroundColor: "transparent !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(118, 75, 162, 0.95)",
  },
}));

const StyledButton = styled(Button)(() => ({
  color: "#fff",
  backgroundColor: "rgba(118, 75, 162, 0.85)",
  padding: "12px 24px",
  borderRadius: 12,
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 600,
  boxShadow: "0 4px 12px rgba(118, 75, 162, 0.25)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(91, 54, 128, 0.95)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(118, 75, 162, 0.35)",
  },
  "&:disabled": {
    backgroundColor: "rgba(118, 75, 162, 0.4)",
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

function ResetPasswordPage() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axiosService.post("/auth/password-reset/confirm/", {
        uid,
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || d?.detail || "This reset link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, rgba(102,126,234,0.12) 0%, rgba(118,75,162,0.08) 100%)",
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <StyledPaper elevation={0}>
          <Box sx={{ mb: 3 }}>
            <img src={logo_alt} alt="Otable" style={{ height: 56, marginBottom: 8 }} />
          </Box>

          {success ? (
            <>
              <CheckCircle sx={{ fontSize: 56, color: "success.main", mb: 2, mx: "auto" }} />
              <Typography variant="h5" fontWeight={600} mb={1}>
                Password updated
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Your password has been reset successfully. You can now sign in with your new password.
              </Typography>
              <StyledButton fullWidth onClick={() => navigate("/login/")}>
                Go to Sign In
              </StyledButton>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <Typography
                variant="h5"
                fontWeight={600}
                mb={0.5}
                sx={{
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Set a new password
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Choose a strong password of at least 8 characters.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2, textAlign: "left" }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <StyledTextField
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                  helperText="At least 8 characters"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end" aria-label={showPassword ? "Hide password" : "Show password"}>
                          {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  label="Confirm Password"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowConfirm((p) => !p)} edge="end" aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}>
                          {showConfirm ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <StyledButton type="submit" fullWidth disabled={loading || !password || !confirm} sx={{ mt: 3 }}>
                {loading ? <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.8)" }} /> : "Reset Password"}
              </StyledButton>

              <Button
                fullWidth
                onClick={() => navigate("/login/")}
                sx={{ mt: 1.5, textTransform: "none", color: "rgba(118,75,162,0.75)", fontWeight: 500, "&:hover": { backgroundColor: "rgba(118,75,162,0.06)" } }}
              >
                ← Back to Sign In
              </Button>
            </form>
          )}
        </StyledPaper>
      </motion.div>
    </Box>
  );
}

export default ResetPasswordPage;
